import Model, { attr, belongsTo } from '@ember-data/model';
import type { ReportSource, VisualComplianceRpsResult } from 'garaje/models/approval-status';
import type { ApprovalStatus } from 'garaje/models/visit-approval';
import type VisitApproval from 'garaje/models/visit-approval';
import type ImmutableTransform from 'garaje/transforms/immutable';
import { BlocklistFilterSource } from 'garaje/utils/enums';
import type TransformValue from 'garaje/utils/transform-value';
import { alias, equal } from 'macro-decorators';
import type { Constructor } from 'type-fest';

export interface BaseReport {
  messages?: {
    failure?: {
      text?: string;
    };
  };
}

export interface ReportWithReason extends BaseReport {
  reason?: string;
}

export interface BlocklistReportFilter {
  filterSourceId: string;
  filterSource: string;
  filterId: string | null;
  keyword: string;
  reason: string;
}

export interface IdScanningNameMatch {
  mismatch?: boolean;
  evaluated?: boolean;
  'veriff-first-name'?: string;
  'veriff-last-name'?: string;
  'comparison-left'?: string;
  'comparison-left-source'?: string;
  'name-action'?: 'updated' | 'removed' | 'kept';
  'applied-first-name'?: string;
  'applied-last-name'?: string;
}

export interface IdScanningFaceMatch {
  evaluated?: boolean;
  mismatch?: boolean;
  held?: boolean;
  verdict?: string;
  similarity?: number | null;
  reason?: string | null;
  threshold?: number;
}

export interface IdScanningReport extends ReportWithReason {
  veriffSessionId?: string;
  bypass?: boolean;
  'name-match'?: IdScanningNameMatch;
  'face-match'?: IdScanningFaceMatch;
}

/**
 * Legal-name override sent to `/review` when approving an ID name-mismatch:
 * edited first/last name, or `remove_legal_name` to skip persisting it.
 */
export interface IdNameReviewPayload {
  legal_first_name?: string;
  legal_last_name?: string;
  remove_legal_name?: boolean;
}

export interface BlocklistReport extends BaseReport {
  matchedFilters: BlocklistReportFilter[];
}

export interface VisualComplianceReport extends BaseReport {
  'alert-level'?: number | null;
  'call-status'?: string;
  count?: number;
  'risk-country'?: boolean;
  'rps-results'?: VisualComplianceRpsResult[];
  message?: string;
}

export interface LocationCapacityReport extends BaseReport {
  'expected-capacity-count'?: number;
  'capacity-limit'?: number;
}

interface ScreenerResultDetails {
  title: string;
  subtitle: string;
  'logo-url': string;
  input: {
    fields: ScreenerResultDetailsField[];
  };
  matches: ScreenerResultDetailsMatch[];
}

export interface ScreenerResultDetailsMatch {
  headers: {
    name: string;
    value: string;
  };
  'alert-level': number | null;
  'visible-fields-count': number | null;
  fields: ScreenerResultDetailsField[];
}

export interface ScreenerResultDetailsField {
  name: string;
  value: string;
  type: 'text' | 'image';
}

export default class ScreenerResult<Report extends BaseReport = BaseReport> extends withScreenerTypeGuards(Model) {
  /**
   * the status of the screener result.
   * - approved - the screener result has been approved.
   * - denied - the screener result has been denied.
   * - pending - the screener result report is pending. bubbles up from the screener report result
   * - review - the screener result is pending review.
   */
  @attr('string') declare status: ApprovalStatus;

  /**
   * the overall decision of the screener. indicates whether the screeners has failed and
   * the result report should be utilized to determine the failure reason.
   * - pass - the screener result has passed. e.g. the person is not on the blocklist.
   * - fail - the screener result has failed. e.g. the person is on the blocklist.
   * - pending - the screener result is pending.
   */
  @attr('string') declare decision: 'pass' | 'fail' | 'pending';

  @attr('immutable') declare report: NonNullable<TransformValue<ImmutableTransform<Report>>>;
  @attr('immutable') declare details: TransformValue<ImmutableTransform<ScreenerResultDetails>>;
  @attr('date') declare reviewedAt: Date | null;
  @attr('string') declare workflowId: string | null;

  @belongsTo('visit-approval', { async: false, inverse: 'screenerResults' }) declare visitApproval: VisitApproval;

  // backwards compatibility for using with existing components and existing report data
  @alias('failureMessage') declare idVerificationFailureMessage: string | undefined;
  @alias('failureMessage') declare tempCheckFailureMessage: string | undefined;

  @equal('status', 'approved') declare isApproved: boolean;
  @equal('status', 'denied') declare isDenied: boolean;
  @equal('status', 'pending') declare isPending: boolean;
  @equal('status', 'review') declare isReview: boolean;

  @equal('decision', 'fail') declare didFail: boolean;

  /**
   * returns true if the screener result is a blocklist match and is only matching on location blocklist filters.
   */
  get isLocationBlocklist(): boolean {
    if (this.isBlocklist()) {
      return this.report.matchedFilters.every((filter) => filter.filterSource === BlocklistFilterSource.LOCATION);
    }
    return false;
  }

  /**
   * returns true if the screener result is a blocklist match and has at least one match on a property blocklist filter.
   */
  get isPropertyBlocklist(): boolean {
    if (this.isBlocklist()) {
      return this.report.matchedFilters.some((filter) => filter.filterSource === BlocklistFilterSource.PROPERTY);
    }
    return false;
  }

  get failureMessage(): string | undefined {
    return this.report.messages?.failure?.text;
  }

  get idScanningFailureMessage(): string {
    if (!this.isIdScanning()) return '';
    return (this.report.veriffSessionId && this.report?.reason) ?? '';
  }
}

/**
 * Adds support for type guards to narrow down the type of the report based on the screener source
 *
 * @param Base the base class
 * @returns a new model class with extended functionality via the `footer`, `documentId` and `documentUrl` fields
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export function withScreenerTypeGuards<TBase extends Constructor<any>>(Base: TBase) {
  class withScreenerTypeGuards extends Base {
    @attr('string') declare screenerSource: ReportSource;

    isBlocklist(): this is TBase & { report: BlocklistReport; screenerSource: 'blacklist' } {
      return this.screenerSource === 'blacklist';
    }

    isVisualCompliance(): this is TBase & { report: VisualComplianceReport; screenerSource: 'visual-compliance' } {
      return this.screenerSource === 'visual-compliance';
    }

    isIdentityVerification(): this is TBase & { report: BaseReport; screenerSource: 'identity-verification' } {
      return this.screenerSource === 'identity-verification';
    }

    isTempScreening(): this is TBase & { report: BaseReport; screenerSource: 'temperature-screening' } {
      return this.screenerSource === 'temperature-screening';
    }

    isVisitorDocuments(): this is TBase & { report: BaseReport; screenerSource: 'visitor-documents' } {
      return this.screenerSource === 'visitor-documents';
    }

    isIdScanning(): this is TBase & { report: IdScanningReport; screenerSource: 'id-scanning' } {
      return this.screenerSource === 'id-scanning';
    }

    isPreRegistrationRequired(): this is TBase & { report: BaseReport; screenerSource: 'pre-registration-required' } {
      return this.screenerSource === 'pre-registration-required';
    }

    isCapacityScreening(): this is TBase & { report: LocationCapacityReport; screenerSource: 'capacity-screening' } {
      return this.screenerSource === 'capacity-screening';
    }

    isOutgoingInvite(): this is TBase & { report: BaseReport; screenerSource: 'requires-approval' } {
      return this.screenerSource === 'requires-approval';
    }

    isPropertyInviteApproval(): this is TBase & { report: BaseReport; screenerSource: 'property-invite-approval' } {
      return this.screenerSource === 'property-invite-approval';
    }

    isConditionalRule(): this is TBase & { report: ReportWithReason; screenerSource: 'conditional-rule' } {
      return this.screenerSource === 'conditional-rule';
    }
  }

  return withScreenerTypeGuards;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'screener-result': ScreenerResult;
  }
}
